import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';
import VolumeSubCharts from './VolumeSubCharts';
import StockFundamentalsDashboard from './StockFundamentalsDashboard';
import { applyVisibleRange, CHART_ZOOM_RANGES, filterSignalBarsForPriceBars } from '../utils/chartTimeRange';
import { buildVolumeMarkers, trendBadgeLabel } from '../utils/volumeChartData';
import { observeChartSize } from '../utils/chartResize';
import './InvestingPage.css';

const ZOOM_RANGES = CHART_ZOOM_RANGES;

const DEFAULT_ZOOM = '1Y';
const DEFAULT_STOCK_SYMBOL = 'NVDA';

function flattenGroups(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.flatMap((group) =>
    (group.symbols || []).map((symbol) => ({
      ...symbol,
      sector_group: symbol.sector_group ?? group.sector_group,
      category: symbol.category ?? group.category,
      yahoo_key: symbol.yahoo_key ?? group.yahoo_key,
    })),
  );
}

function fmtWeight(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(2)}%`;
}

function fmtPrice(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function rowKey(row) {
  return `${row.sector_group}-${row.symbol}`;
}

function InvestingPage() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const seriesMarkersRef = useRef(null);
  const [priceChart, setPriceChart] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [bars, setBars] = useState([]);
  const [signalBars, setSignalBars] = useState([]);
  const [latestTrend, setLatestTrend] = useState(null);
  const [latestObv, setLatestObv] = useState(null);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeError, setVolumeError] = useState(null);
  const [showVolumePanel, setShowVolumePanel] = useState(true);
  const [zoomRange, setZoomRange] = useState(DEFAULT_ZOOM);
  const zoomRangeRef = useRef(DEFAULT_ZOOM);

  useEffect(() => {
    zoomRangeRef.current = zoomRange;
  }, [zoomRange]);

  useEffect(() => {
    let cancelled = false;

    async function loadSymbols() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/investing/stocks/symbols');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setRows(flattenGroups(data.groups || []));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load symbols');
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSymbols();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedRow?.symbol || !chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        axisPressedMouseMove: true,
        axisDoubleClickReset: true,
        mouseWheel: true,
        pinch: true,
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C',
      downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C',
      wickDownColor: '#C0571F',
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
    setPriceChart(chart);

    const disconnectResize = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height });
    });

    return () => {
      disconnectResize();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      seriesMarkersRef.current = null;
      setPriceChart(null);
    };
  }, [selectedRow?.symbol]);

  useEffect(() => {
    if (!bars.length || !candleSeriesRef.current) return;
    candleSeriesRef.current.setData(bars);
    applyVisibleRange(chartRef.current, bars, zoomRangeRef.current);
  }, [bars]);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el || !selectedRow) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectedRow]);

  useEffect(() => {
    if (!selectedRow) {
      setBars([]);
      setSignalBars([]);
      setLatestTrend(null);
      setLatestObv(null);
      setChartError(null);
      setVolumeError(null);
      candleSeriesRef.current?.setData([]);
      seriesMarkersRef.current?.setMarkers([]);
      return;
    }

    let cancelled = false;

    async function loadChartData() {
      setChartLoading(true);
      setVolumeLoading(true);
      setChartError(null);
      setVolumeError(null);
      setBars([]);
      candleSeriesRef.current?.setData([]);
      const symbol = encodeURIComponent(selectedRow.symbol);
      let loadedPriceBars = [];
      try {
        const priceResponse = await fetch(`/api/investing/prices/${symbol}`);
        if (!priceResponse.ok) {
          let message = `Prices HTTP ${priceResponse.status}`;
          try {
            const errBody = await priceResponse.json();
            message = errBody.error || message;
          } catch {
            // non-JSON body
          }
          throw new Error(message);
        }

        const priceData = await priceResponse.json();
        if (cancelled) return;

        loadedPriceBars = priceData.bars || [];
        setBars(loadedPriceBars);
      } catch (err) {
        if (!cancelled) {
          setChartError(err.message || 'Failed to load price history');
          setBars([]);
          candleSeriesRef.current?.setData([]);
        }
      } finally {
        if (!cancelled) {
          setChartLoading(false);
        }
      }

      try {
        const signalResponse = await fetch(`/api/investing/volume/signals/${symbol}`);
        if (cancelled) return;

        if (signalResponse.ok) {
          const signalData = await signalResponse.json();
          if (cancelled) return;
          const nextSignals = signalData.bars || [];
          setSignalBars(nextSignals);
          setLatestTrend(signalData.latest_trend || null);
          setLatestObv(signalData.latest_obv || null);
          const priceTimes = loadedPriceBars.length
            ? filterSignalBarsForPriceBars(nextSignals, loadedPriceBars)
            : nextSignals;
          seriesMarkersRef.current?.setMarkers(buildVolumeMarkers(priceTimes));
          setVolumeError(null);
        } else if (signalResponse.status === 404) {
          setSignalBars([]);
          setLatestTrend(null);
          setLatestObv(null);
          seriesMarkersRef.current?.setMarkers([]);
          setVolumeError(null);
        } else {
          setSignalBars([]);
          setLatestTrend(null);
          setLatestObv(null);
          seriesMarkersRef.current?.setMarkers([]);
          setVolumeError(
            signalResponse.status === 503
              ? 'Volume views not deployed on server'
              : `Volume signals HTTP ${signalResponse.status}`,
          );
        }
      } catch (err) {
        if (!cancelled) {
          setSignalBars([]);
          setLatestTrend(null);
          setLatestObv(null);
          seriesMarkersRef.current?.setMarkers([]);
          setVolumeError(err.message || 'Failed to load volume signals');
        }
      } finally {
        if (!cancelled) {
          setVolumeLoading(false);
        }
      }
    }

    loadChartData();
    return () => {
      cancelled = true;
    };
  }, [selectedRow]);

  useEffect(() => {
    if (!signalBars.length) {
      seriesMarkersRef.current?.setMarkers([]);
      return;
    }
    const aligned = filterSignalBarsForPriceBars(signalBars, bars);
    seriesMarkersRef.current?.setMarkers(buildVolumeMarkers(aligned));
  }, [bars, signalBars]);

  const handleZoomChange = useCallback((rangeLabel) => {
    zoomRangeRef.current = rangeLabel;
    setZoomRange(rangeLabel);
    applyVisibleRange(chartRef.current, bars, rangeLabel);
  }, [bars]);

  const sectorOptions = useMemo(
    () => [...new Set(rows.map((row) => row.sector_group))].sort(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const symQ = symbolFilter.trim().toUpperCase();
    return rows.filter((row) => {
      if (groupFilter && row.sector_group !== groupFilter) return false;
      if (symQ && !row.symbol.toUpperCase().includes(symQ)) return false;
      return true;
    });
  }, [rows, symbolFilter, groupFilter]);

  useEffect(() => {
    if (filteredRows.length === 0) return;
    setSelectedRow((prev) => {
      if (prev && filteredRows.some((row) => rowKey(row) === rowKey(prev))) {
        return prev;
      }
      const preferred = filteredRows.find(
        (row) => row.symbol.toUpperCase() === DEFAULT_STOCK_SYMBOL,
      );
      return preferred || filteredRows[0];
    });
  }, [filteredRows]);

  const latestBar = bars.length > 0 ? bars[bars.length - 1] : null;
  const trendLabel = trendBadgeLabel(latestTrend);

  function handleRowClick(row) {
    zoomRangeRef.current = DEFAULT_ZOOM;
    setZoomRange(DEFAULT_ZOOM);
    setSelectedRow(row);
  }

  return (
    <div className="investing-page page-content">
      <div className="investing-left">
        <div className="investing-controls investing-controls-minimal">
          <span className="investing-count">
            {loading ? 'Loading…' : `${filteredRows.length} symbols`}
          </span>
        </div>

        {error && (
          <div className="investing-error">
            Could not load symbols: {error}. Is investing_backend running on port 5001?
          </div>
        )}

        <div className="investing-table-wrap">
          <table className="investing-table">
            <thead>
              <tr>
                <th className="investing-th-filter">
                  <span className="investing-th-title">Symbol</span>
                  <input
                    type="search"
                    className="investing-col-filter"
                    placeholder="Filter…"
                    value={symbolFilter}
                    onChange={(e) => setSymbolFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Filter by symbol"
                  />
                </th>
                <th>Name</th>
                <th className="investing-th-filter investing-th-filter-group">
                  <span className="investing-th-title">Group</span>
                  <select
                    className="investing-col-filter"
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Filter by group"
                  >
                    <option value="">All groups</option>
                    {sectorOptions.map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </th>
                <th>Category</th>
                <th>Price</th>
                <th>Analyst rating</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="investing-empty">
                    No symbols match the current filters.
                  </td>
                </tr>
              )}
              {filteredRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={
                    selectedRow && rowKey(selectedRow) === rowKey(row)
                      ? 'investing-row-selected'
                      : ''
                  }
                  onClick={() => handleRowClick(row)}
                >
                  <td className="investing-symbol">{row.symbol}</td>
                  <td>{row.name || '—'}</td>
                  <td>{row.sector_group}</td>
                  <td>{row.category}</td>
                  <td className="investing-mono">{fmtPrice(row.close)}</td>
                  <td>{row.analyst_rating || '—'}</td>
                  <td className="investing-mono">{fmtWeight(row.market_weight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="investing-right">
        {!selectedRow && (
          <div className="investing-placeholder">
            <span>Select a symbol to view price history</span>
          </div>
        )}

        {selectedRow && (
          <>
            <div className="investing-chart-header">
              <div className="investing-chart-title">
                <span className="investing-chart-symbol">{selectedRow.symbol}</span>
                <span className="investing-chart-name">{selectedRow.name || '—'}</span>
              </div>
              <div className="investing-chart-meta">
                <span>{selectedRow.sector_group}</span>
                {latestBar && (
                  <span className="investing-chart-price">
                    {fmtPrice(latestBar.close)}
                    <span className="investing-chart-date"> · {latestBar.time}</span>
                  </span>
                )}
                {bars.length > 0 && (
                  <span className="investing-chart-bars">{bars.length} days</span>
                )}
                {trendLabel && (
                  <span className={`investing-volume-trend${latestTrend?.confirmed ? ' confirmed' : ''}`}>
                    {trendLabel}
                  </span>
                )}
              </div>
              <div className="investing-zoom-toolbar">
                <button
                  type="button"
                  className={`volume-panel-toggle${showVolumePanel ? ' active' : ''}`}
                  onClick={() => setShowVolumePanel((v) => !v)}
                >
                  <span className="volume-panel-toggle-dot" />
                  Volume / OBV
                </button>
                <span className="investing-zoom-label">Range</span>
                {ZOOM_RANGES.map(({ label }) => (
                  <button
                    key={label}
                    type="button"
                    className={`investing-zoom-btn${zoomRange === label ? ' active' : ''}`}
                    onClick={() => handleZoomChange(label)}
                    disabled={chartLoading || bars.length === 0}
                  >
                    {label}
                  </button>
                ))}
                <span className="investing-zoom-hint">Scroll to zoom · drag to pan</span>
              </div>
            </div>

            {chartError && (
              <div className="investing-error">
                Could not load prices for {selectedRow.symbol}: {chartError}
              </div>
            )}

            {chartLoading && (
              <div className="investing-chart-loading">Loading chart…</div>
            )}
            {volumeError && (
              <div className="investing-chart-loading investing-volume-warn">{volumeError}</div>
            )}
          </>
        )}

        <div className={`investing-charts-area${selectedRow ? '' : ' investing-chart-hidden'}`}>
          <div
            ref={chartContainerRef}
            className={`investing-chart-wrap${showVolumePanel ? ' with-volume' : ''}`}
          />
          {selectedRow && (
            <VolumeSubCharts
              visible={showVolumePanel}
              loading={volumeLoading}
              signalBars={signalBars}
              latestTrend={latestTrend}
              latestObv={latestObv}
              parentChart={priceChart}
              priceBars={bars}
              zoomRange={zoomRange}
            />
          )}
        </div>

        {selectedRow && (
          <StockFundamentalsDashboard symbol={selectedRow.symbol} />
        )}
      </div>
    </div>
  );
}

export default InvestingPage;
